<script setup>
import {onBeforeMount, onUpdated, ref, toRef} from "vue";
import GroupCodeBox from "@/components/common/input/groupCodeBox.vue";
import {useComStore, useLanguageStore} from "@/pinia";
import {useToast} from "@/composables/useToast";

const toast = useToast();
const languageStore = useLanguageStore();

const props = defineProps(['detailModal', 'codeValue'])
const emits = defineEmits(['set_detailModal'])
const isReadonly = ref(true);

const codeId = ref();
const codeNm = ref();
const grpCode = ref();
const ord = ref();
const memo = ref();
const useYn = ref();

const comStore = useComStore();

const update = ()=>{
  if(isReadonly.value){
    isReadonly.value = false;
  }else{
    if(!codeNm.value || codeNm.value.trim() === '') {
      toast.warning(languageStore.get('fillInBlank'));
      return;
    }
    const data = {
      "codeId" : codeId.value,
      "codeNm" : codeNm.value,
      "grpCode" : grpCode.value,
      "ord" : ord.value,
      "memo": memo.value,
      "useYn" : useYn.value
    }

    comStore.updateCommonCode(data)
    close();
    clear();
  }
}
const clear = ()=>{
  codeId.value =null;
  codeNm.value = null;
  grpCode.value = null;
  ord.value = null;
  memo.value = null;
  useYn.value =null;
  isReadonly.value =false;
}
const close = ()=>{
  emits('set_detailModal', false);
  clear();
}
onUpdated(()=>{
  if(props.codeValue){
    codeId.value = props.codeValue.codeId;
    codeNm.value = props.codeValue.codeNm;
    grpCode.value =props.codeValue.grpCode;
    ord.value = props.codeValue.ord;
    memo.value =props.codeValue.memo;
    useYn.value = props.codeValue.useYn;
  }
})
</script>

<template>
  <v-dialog
      :model-value="detailModal"
      @update:model-value="close"
      width="auto"
  >
    <v-card
        width="600"
        :title="isReadonly ? languageStore.get('details') : languageStore.get('edit')"
        :subtitle="isReadonly ? languageStore.get('clickEditToModify') : languageStore.get('clickSaveToSave')"
    >
      <v-card-text>
        <v-text-field
            v-model="codeId"
            :label="languageStore.get('codeId')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            readonly
            class="mb-4"
        ></v-text-field>

        <v-text-field
            v-model="codeNm"
            :label="languageStore.get('codeName')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="isReadonly"
            class="mb-4"
        ></v-text-field>

        <group-code-box
            :grp-code="grpCode"
            :readonly="isReadonly"
            class="mb-4"
        ></group-code-box>

        <v-text-field
            v-model="ord"
            :label="languageStore.get('order')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="isReadonly"
            class="mb-4"
        ></v-text-field>

        <v-text-field
            v-model="memo"
            :label="languageStore.get('memo')"
            variant="outlined"
            density="compact"
            hide-details="auto"
            :readonly="isReadonly"
            class="mb-4"
        ></v-text-field>

        <v-radio-group
            inline
            v-model="useYn"
            :label="languageStore.get('useYn')"
            :readonly="isReadonly"
            hide-details
        >
          <v-radio :label="languageStore.get('use')" value="1"></v-radio>
          <v-radio :label="languageStore.get('notUse')" value="0"></v-radio>
        </v-radio-group>
      </v-card-text>

      <v-card-actions class="modal-actions">
        <v-spacer></v-spacer>
        <v-btn color="primary" variant="flat" @click="update">
          {{ isReadonly ? languageStore.get('edit') : languageStore.get('save') }}
        </v-btn>
        <v-btn variant="outlined" @click="close">{{ languageStore.get('close') }}</v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<style scoped>
/* 모달 헤더 스타일 */
:deep(.v-card-item) {
  background-color: #1A3A5C !important;
  padding: 0 !important;
}

:deep(.v-card-title) {
  background-color: #1A3A5C !important;
  color: #FFFFFF !important;
  padding: 16px 24px 8px !important;
  font-weight: 600 !important;
  font-size: 18px !important;
}

:deep(.v-card-subtitle) {
  padding: 0 24px 16px !important;
  background-color: #1A3A5C !important;
  color: rgba(255, 255, 255, 0.8) !important;
  opacity: 1 !important;
}

/* 모달 바디 - Vuetify 기본 padding 제거 */
:deep(.v-card-text) {
  padding: 24px !important;
}

/* v-card-text 내 입력 필드 간격 */
.v-card-text :deep(.v-input) {
  margin-bottom: 16px;
}

.v-card-text :deep(.v-input:last-child),
.v-card-text :deep(.v-radio-group) {
  margin-bottom: 0;
}

/* 모달 액션 - Vuetify 기본 padding 제거 */
:deep(.v-card-actions.modal-actions) {
  padding: 16px 24px !important;
  border-top: 1px solid #DEE2E6;
  gap: 8px;
}
</style>